import logoBrandStarter from '../assets/logo_brandstarter.png';
import { useState, useRef, useEffect } from 'react';
import { api } from '../services/api';
import { CSS as BASE_CSS } from './ModulePersona';

// Le cours, le quiz et le chat réutilisent le style validé du module Persona (classes mp-).
export const CSS = `
  .fq { background: #fff; border: 1px solid var(--bs-line); border-radius: 20px; overflow: hidden; max-width: 640px; margin: 0 auto; }
  .fq-head { background: var(--bs-green); color: #F6F2EC; padding: 22px 22px 20px; }
  .fq-kick { font-size: 10.5px; letter-spacing: .14em; text-transform: uppercase; font-weight: 700; opacity: .7; }
  .fq-brand { font-family: 'Playfair Display', serif; font-size: 26px; font-weight: 700; margin-top: 6px; line-height: 1.15; }
  .fq-promise { background: var(--bs-orange); color: #fff; padding: 20px 22px; }
  .fq-promise .fq-kick { opacity: .85; }
  .fq-promise p { font-family: 'Playfair Display', serif; font-size: 22px; font-weight: 700; line-height: 1.3; margin-top: 8px; }
  .fq-body { padding: 8px 22px 22px; }
  .fq-sec { margin-top: 18px; }
  .fq-lab { font-size: 10.5px; letter-spacing: .13em; text-transform: uppercase; font-weight: 700; color: var(--bs-orange); margin-bottom: 6px; }
  .fq-txt { font-size: 15px; line-height: 1.6; color: #333; }
  .fq-phrase { background: #F6F2EC; border-radius: 14px; padding: 14px 16px; font-size: 14.5px; line-height: 1.65; color: #333; font-style: italic; }
  .fq-chips { display: flex; flex-wrap: wrap; gap: 8px; }
  .fq-chip { background: #EAF1EE; color: var(--bs-green); border-radius: 999px; padding: 8px 13px; font-size: 13.5px; font-weight: 600; line-height: 1.3; }
  .fq-chip.o { background: #FDE6DE; color: #B8391A; }
  .fq-list { margin: 0; padding: 0; list-style: none; }
  .fq-list li { padding: 8px 0 8px 22px; position: relative; font-size: 14.5px; line-height: 1.55; color: #333; border-bottom: 1px solid #F0EBE3; }
  .fq-list li:last-child { border-bottom: 0; }
  .fq-list li::before { content: ''; position: absolute; left: 4px; top: 16px; width: 8px; height: 8px; border-radius: 50%; background: var(--bs-green); }
  .fq-score { display: flex; align-items: center; gap: 14px; background: #EAF1EE; border-radius: 14px; padding: 14px 16px; }
  .fq-score b { font-family: 'Playfair Display', serif; font-size: 30px; color: var(--bs-green); line-height: 1; }
  .fq-score span { font-size: 13px; color: #444; line-height: 1.45; }
  .fq-foot { display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 14px 22px; border-top: 1px solid var(--bs-line); background: #FBF9F5; }
  .fq-foot img { height: 26px; width: auto; }
  .fq-sec { position: relative; }
  .fq-cp { position: absolute; right: 0; top: -2px; background: none; border: 0; color: var(--bs-orange); font-weight: 700; font-size: 12.5px; cursor: pointer; font-family: 'Inter', sans-serif; min-height: 32px; }
  .fq-dl { background: var(--bs-green); color: #F6F2EC; border: 0; border-radius: 999px; min-height: 44px; padding: 0 18px; font-weight: 700; font-size: 13.5px; font-family: 'Inter', sans-serif; cursor: pointer; }
`;

// ─── COURS ─────────────────────────────────────────────────────
const FORMATION = [
  {
    num: 'Partie 1',
    titre: "Pourquoi on ne te choisit pas encore",
    content: () => (
      <>
        <p>Quand une marque n'a pas de place claire dans l'esprit des gens, il reste un seul critère pour la comparer : <strong>le prix.</strong> Et sur le prix, un créateur seul perd presque toujours.</p>
        <div className="mp-highlight orange">
          Le positionnement, c'est la place que ta marque occupe dans la tête de ton client, par rapport à tout ce qu'il pourrait choisir d'autre.
        </div>
        <p>Cette idée vient d'un classique du marketing : <strong>« Positioning: The Battle for Your Mind »</strong>, d'Al Ries et Jack Trout (1981). Leur constat tient toujours : on ne se bat pas sur un marché, on se bat dans la tête des gens.</p>
        <p>Un bon positionnement répond à une seule question, que ton client se pose sans le dire : <strong>« Pourquoi toi, plutôt qu'un autre ? »</strong></p>
      </>
    )
  },
  {
    num: 'Partie 2',
    titre: "Tes vrais concurrents ne sont pas ceux que tu crois",
    content: () => (
      <>
        <p>Quand on pense « concurrents », on pense aux marques qui font la même chose que nous. Ton client, lui, pense en <strong>alternatives</strong> : tout ce qu'il peut faire pour résoudre son problème.</p>
        <div className="mp-fw-steps">
          <div className="mp-fw-step"><div className="mp-fw-num">1</div><div className="mp-fw-content"><strong>Les concurrents directs</strong><span>Ceux qui vendent la même chose que toi, à la même cible.</span></div></div>
          <div className="mp-fw-step"><div className="mp-fw-num">2</div><div className="mp-fw-content"><strong>Les concurrents indirects</strong><span>Ceux qui résolvent le même problème autrement.</span></div></div>
          <div className="mp-fw-step"><div className="mp-fw-num">3</div><div className="mp-fw-content"><strong>Le statu quo</strong><span>Ne rien faire, ou se débrouiller seul. C'est souvent ton plus gros concurrent.</span></div></div>
        </div>
        <div className="mp-highlight">
          La spécialiste du positionnement April Dunford le résume ainsi : on commence toujours par se demander <strong>ce que ton client ferait si tu n'existais pas.</strong>
        </div>
      </>
    )
  },
  {
    num: 'Partie 3',
    titre: "Ta différence : une caractéristique, un bénéfice, une preuve",
    content: () => (
      <>
        <p>Être « différent » ne suffit pas. Une différence ne vaut que si elle passe trois tests.</p>
        <div className="mp-fw-steps">
          <div className="mp-fw-step"><div className="mp-fw-num">1</div><div className="mp-fw-content"><strong>Elle est vraie et concrète</strong><span>« Coulé à la main en petites séries » est concret. « De qualité » ne l'est pas.</span></div></div>
          <div className="mp-fw-step"><div className="mp-fw-num">2</div><div className="mp-fw-content"><strong>Elle apporte un bénéfice que ton client valorise</strong><span>Ce qui compte pour lui, pas pour toi.</span></div></div>
          <div className="mp-fw-step"><div className="mp-fw-num">3</div><div className="mp-fw-content"><strong>Tu peux la prouver</strong><span>Expérience, résultats, méthode, témoignages, chiffres réels.</span></div></div>
        </div>
        <div className="mp-highlight orange">
          Une différence que tu ne peux pas prouver n'est pas une différence, c'est une promesse en l'air. Les clients le sentent.
        </div>
      </>
    )
  },
  {
    num: 'Partie 4',
    titre: "La promesse et la phrase de positionnement",
    content: () => (
      <>
        <p>Tout ce travail se résume en deux phrases, que tu pourras réutiliser partout : sur ton site, dans ta bio, à l'oral.</p>
        <div className="mp-highlight">
          <strong>La promesse :</strong> un seul bénéfice, simple, vérifiable, en moins de 12 mots.
        </div>
        <div className="mp-highlight orange">
          <strong>La phrase de positionnement :</strong><br/>« Pour [ta cible] qui [son besoin], [ta marque] est [ta catégorie] qui [ton bénéfice clé]. Contrairement à [l'alternative], [ta différence]. »
        </div>
        <div className="mp-clara">
          <div className="mp-clara-avatar">C</div>
          <div className="mp-clara-bubble">
            <div className="mp-clara-name">Clara</div>
            <div className="mp-clara-text">On va répondre à 8 questions, une par une. Je reprends ce que tu m'as déjà dit sur ton idée et ton client, tu n'as rien à répéter. Et je te challenge si une réponse reste floue.</div>
          </div>
        </div>
      </>
    )
  }
];

// ─── QUIZ ──────────────────────────────────────────────────────
const QUIZ = [
  {
    q: "Qu'est-ce qu'un positionnement ?",
    opts: ["Le logo et les couleurs de la marque", "La place de ta marque dans l'esprit du client par rapport aux alternatives", "Le prix de ton offre", "Le nombre d'abonnés sur les réseaux"],
    correct: 1,
    feedback: "Exact. Le positionnement se joue dans la tête du client, pas dans ton logo."
  },
  {
    q: "Quel est souvent le plus gros concurrent d'un créateur ?",
    opts: ["La plus grande marque du secteur", "Un créateur plus jeune", "Le statu quo : ne rien faire ou se débrouiller seul", "Les réseaux sociaux"],
    correct: 2,
    feedback: "Oui. Beaucoup de clients ne choisissent aucune marque, ils gardent leur solution actuelle."
  },
  {
    q: "Quand une différence est-elle utile ?",
    opts: ["Quand elle est originale", "Quand elle apporte un bénéfice que le client valorise et que tu peux la prouver", "Quand les concurrents ne la font pas", "Quand elle te plaît"],
    correct: 1,
    feedback: "Exact. Originale ne suffit pas : il faut un bénéfice pour le client et une preuve."
  },
  {
    q: "Qu'est-ce qu'une bonne promesse de marque ?",
    opts: ["Une liste complète de tes services", "Un slogan qui sonne bien", "Un seul bénéfice, précis, simple et vérifiable", "Une phrase qui parle à tout le monde"],
    correct: 2,
    feedback: "Oui. Une promesse précise se retient et se vérifie. Une promesse floue ne dit rien."
  }
];

// ─── QUESTIONS DE LA CONVERSATION ──────────────────────────────
const QUESTIONS_CONV = [
  { bloc: 'Alternatives', q: (c) => `Quand ${c.persona} a le problème que tu résous, que fait-il ou elle concrètement aujourd'hui, avant de te connaître ? Même si c'est "rien" ou "je me débrouille seul".` },
  { bloc: 'Alternatives', q: (c) => `Cite 2 ou 3 concurrents ou alternatives que ${c.persona} compare avec ${c.brand} : des marques, d'autres solutions, ou le faire soi-même.` },
  { bloc: 'Alternatives', q: () => `Honnêtement, qu'est-ce que ces alternatives font bien ? Ce que ton client apprécie chez elles.` },
  { bloc: 'Différence', q: (c) => `Qu'est-ce que ${c.brand} fait que les autres ne font pas, ou font moins bien ? Donne-moi 2 ou 3 choses concrètes.` },
  { bloc: 'Différence', q: (c) => `Parmi ces différences, laquelle compte le plus pour ${c.persona} ? Et pourquoi ?` },
  { bloc: 'Différence', q: () => `Quelle preuve peux-tu montrer pour que ce soit crédible ? Expérience, résultats, méthode, témoignages, chiffres réels.` },
  { bloc: 'Promesse', q: (c) => `Si ${c.persona} ne devait retenir qu'une chose de ${c.brand}, laquelle ? Écris ta promesse comme tu la dirais à voix haute, en une phrase.` },
  { bloc: 'Promesse', q: () => `Dans quelle catégorie veux-tu qu'on te range ? Et à qui ne t'adresses-tu surtout pas ?` },
];

// ─── OUTILS ────────────────────────────────────────────────────
function getCtx(contextePrecedent) {
  const list = Array.isArray(contextePrecedent) ? contextePrecedent : [];
  const find = (num) => list.find(c => Number(c.numeroModule) === num)?.contenu || '';
  const brand = (find(1).match(/NOM_MARQUE:\s*(.+)/)?.[1] || '').trim() || 'ta marque';
  const persona = (find(2).match(/PERSONA_PRENOM:\s*(.+)/)?.[1] || '').trim() || 'ton client idéal';
  return { brand, persona };
}

function parseSynthese(text) {
  const get = (key) => (text.match(new RegExp(`^\\s*${key}:\\s*(.+)$`, 'm'))?.[1] || '').replace(/\*\*/g, '').trim();
  const list = (key) => get(key).split('|').map(s => s.trim()).filter(Boolean);
  return {
    concurrents: list('POS_CONCURRENTS'), statuQuo: get('POS_STATU_QUO'), forces: get('POS_FORCES_CONCURRENTS'),
    differences: list('POS_DIFFERENCES'), diffCle: get('POS_DIFF_CLE'), valeur: get('POS_VALEUR'),
    preuves: list('POS_PREUVES'), categorie: get('POS_CATEGORIE'), antiCible: get('POS_ANTICIBLE'),
    promesse: get('POS_PROMESSE'), phrase: get('POS_PHRASE'),
    score: parseInt(get('COHERENCE_SCORE'), 10) || null, ok: list('COHERENCE_OK'), warn: list('COHERENCE_WARN'),
    synthese: get('SYNTHESE'),
  };
}

function FichePositionnement({ synthese, brand }) {
  const d = parseSynthese(synthese);
  const ref = useRef(null);
  const download = () => {
    const el = ref.current; if (!el) return;
    const s = document.createElement('script');
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
    document.head.appendChild(s);
    s.onload = () => {
      window.html2canvas(el, { scale: 2, useCORS: true, backgroundColor: '#ffffff' }).then(canvas => {
        const a = document.createElement('a');
        a.download = `positionnement-${(brand || 'marque').toLowerCase().replace(/\s+/g, '-')}.png`;
        a.href = canvas.toDataURL('image/png'); a.click();
      });
    };
  };
  return (
    <div ref={ref} className="fq">
      <div className="fq-head">
        <div className="fq-kick">Positionnement et promesse</div>
        <div className="fq-brand">{brand}</div>
      </div>
      {d.promesse && (
        <div className="fq-promise"><div className="fq-kick">Promesse de marque</div><p>{d.promesse}</p></div>
      )}
      <div className="fq-body">
        {d.phrase && <div className="fq-sec"><div className="fq-lab">Phrase de positionnement</div><div className="fq-phrase">{d.phrase}</div></div>}
        {d.diffCle && <div className="fq-sec"><div className="fq-lab">Ta différence clé</div><div className="fq-txt"><b>{d.diffCle}</b>{d.valeur ? <><br />{d.valeur}</> : null}</div></div>}
        {d.preuves.length > 0 && <div className="fq-sec"><div className="fq-lab">Tes preuves</div><ul className="fq-list">{d.preuves.map((p, i) => <li key={i}>{p}</li>)}</ul></div>}
        {d.concurrents.length > 0 && <div className="fq-sec"><div className="fq-lab">Alternatives et concurrents</div><div className="fq-chips">{d.concurrents.map((c, i) => <span key={i} className="fq-chip">{c}</span>)}</div>{d.statuQuo && <p className="fq-txt" style={{ marginTop: 10 }}><b>Statu quo :</b> {d.statuQuo}</p>}</div>}
        {d.differences.length > 0 && <div className="fq-sec"><div className="fq-lab">Ce qui te distingue</div><ul className="fq-list">{d.differences.map((p, i) => <li key={i}>{p}</li>)}</ul></div>}
        {(d.categorie || d.antiCible) && <div className="fq-sec"><div className="fq-lab">Catégorie et limites</div>{d.categorie && <p className="fq-txt"><b>On te range dans :</b> {d.categorie}</p>}{d.antiCible && <p className="fq-txt" style={{ marginTop: 6 }}><b>Tu ne t'adresses pas à :</b> {d.antiCible}</p>}</div>}
        {d.score != null && (
          <div className="fq-sec"><div className="fq-score"><b>{d.score}</b><span>Score de cohérence sur 100.{d.warn[0] ? ` À surveiller : ${d.warn[0]}.` : ''}</span></div></div>
        )}
        {d.synthese && <div className="fq-sec"><div className="fq-lab">En résumé</div><div className="fq-txt">{d.synthese}</div></div>}
      </div>
      <div className="fq-foot">
        <img src={logoBrandStarter} alt="BrandStarter" />
        <button className="fq-dl" onClick={download}>Télécharger PNG</button>
      </div>
    </div>
  );
}

// ─── COMPOSANT PRINCIPAL ───────────────────────────────────────
export function ModulePositionnement({ moduleValide, syntheseExistante, onValidate, contextePrecedent }) {
  const ctx = getCtx(contextePrecedent);
  const [phase, setPhase] = useState(moduleValide ? 'done' : 'formation');
  const [formationStep, setFormationStep] = useState(0);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [quizOk, setQuizOk] = useState(false);
  const [convMessages, setConvMessages] = useState([]);
  const [convInput, setConvInput] = useState('');
  const [convLoading, setConvLoading] = useState(false);
  const [convStep, setConvStep] = useState(0);
  const [synthese, setSynthese] = useState(syntheseExistante || '');
  const [error, setError] = useState('');
  const chatEndRef = useRef(null);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [convMessages, error]);
  useEffect(() => { window.scrollTo(0, 0); }, [phase]);

  const qText = (i) => { const q = QUESTIONS_CONV[i].q; return typeof q === 'function' ? q(ctx) : q; };

  useEffect(() => {
    if (phase === 'conversation' && convMessages.length === 0) {
      setConvMessages([{ role: 'assistant', content: `On y va, on construit ta place sur le marché. ${qText(0)}` }]);
    }
    // eslint-disable-next-line
  }, [phase]);

  const totalSteps = FORMATION.length;
  const progress = phase === 'formation' ? ((formationStep + 1) / (totalSteps + 2)) * 100
    : phase === 'quiz' ? ((totalSteps + 1) / (totalSteps + 2)) * 100
    : phase === 'conversation' ? ((totalSteps + 1.5) / (totalSteps + 2)) * 100 : 100;

  const submitQuiz = () => {
    if (!QUIZ.every((_, i) => quizAnswers[i] !== undefined)) { setError('Réponds à toutes les questions avant de continuer.'); return; }
    setQuizSubmitted(true);
    setQuizOk(QUIZ.every((q, i) => quizAnswers[i] === q.correct));
    setError('');
  };
  const retryQuiz = () => { setQuizAnswers({}); setQuizSubmitted(false); setQuizOk(false); };

  const buildChallengePrompt = (step, answer, nextStep) => {
    const blocChange = QUESTIONS_CONV[nextStep].bloc !== QUESTIONS_CONV[step].bloc;
    return `Tu es Clara, guide IA de BrandStarter. Tu construis le positionnement de la marque ${ctx.brand} avec son créateur, en 8 questions. La cible est ${ctx.persona}.

ÉTAPE ACTUELLE : ${step + 1}/8 (bloc : ${QUESTIONS_CONV[step].bloc})
QUESTION POSÉE : "${qText(step)}"
RÉPONSE DE L'UTILISATEUR : "${answer}"
${blocChange ? `\nTu passes au bloc "${QUESTIONS_CONV[nextStep].bloc}". Annonce ce changement de manière fluide, en une demi-phrase.\n` : ''}
TON RÔLE :
1. Réagis à la réponse en 1 ou 2 phrases. Si elle est vague ("de la qualité", "un service personnalisé", "innovant"), challenge avec bienveillance et demande un exemple concret. Si elle est précise, valide chaleureusement.
2. Pose la question suivante : "${qText(nextStep)}"

RÈGLES STRICTES :
- Tutoiement obligatoire
- Jamais de tiret long, utilise ":" ou une virgule
- Ne redemande jamais le nom de la marque ni ce qui est déjà dans le contexte des modules précédents
- Maximum 4 lignes au total`;
  };

  const buildSynthesePrompt = (msgs) => {
    const qa = msgs.filter(m => m.role === 'user').map((m, i) => `Q${i + 1} (${qText(i)}) : ${m.content}`).join('\n');
    return `Tu es Clara, guide IA de BrandStarter. À partir des 8 réponses du créateur, génère la fiche de positionnement de la marque ${ctx.brand} (cible : ${ctx.persona}).

RÉPONSES COLLECTÉES :
${qa}

Génère la fiche EXACTEMENT dans ce format (une clé par ligne, valeurs concrètes tirées des réponses, jamais de généralités) :

POS_CONCURRENTS: [alternative1 | alternative2 | alternative3]
POS_STATU_QUO: [ce que fait la cible aujourd'hui sans la marque]
POS_FORCES_CONCURRENTS: [ce que les alternatives font bien]
POS_DIFFERENCES: [différence1 | différence2 | différence3]
POS_DIFF_CLE: [la différence qui compte le plus pour la cible]
POS_VALEUR: [le bénéfice concret pour la cible]
POS_PREUVES: [preuve1 | preuve2 | preuve3]
POS_CATEGORIE: [catégorie]
POS_ANTICIBLE: [à qui la marque ne s'adresse pas]
POS_PROMESSE: [promesse de marque, 12 mots maximum, un seul bénéfice]
POS_PHRASE: [Pour ... qui ..., ${ctx.brand} est ... qui ... Contrairement à ..., ...]
COHERENCE_SCORE: [note de 0 à 100 selon la précision et la cohérence des réponses]
COHERENCE_OK: [point fort 1 | point fort 2]
COHERENCE_WARN: [point à améliorer 1 | point à améliorer 2]
SYNTHESE: [synthèse en 3 phrases]

IMPORTANT : respecte exactement ce format, tutoiement dans la synthèse, aucun tiret long, aucun markdown.`;
  };

  const sendConvMessage = async () => {
    if (!convInput.trim() || convLoading) return;
    const userInput = convInput.trim();
    const newMsgs = [...convMessages, { role: 'user', content: userInput }];
    setConvMessages(newMsgs);
    setConvInput('');
    setConvLoading(true);
    setError('');
    try {
      const nextStep = convStep + 1;
      if (nextStep < QUESTIONS_CONV.length) {
        const res = await api.chatIA(4, { conversation: [{ role: 'user', content: buildChallengePrompt(convStep, userInput, nextStep) }], tourActuel: convStep, contextePrecedent });
        setConvMessages(prev => [...prev, { role: 'assistant', content: res.message }]);
        setConvStep(nextStep);
      } else {
        const res = await api.chatIA(4, { conversation: [{ role: 'user', content: buildSynthesePrompt(newMsgs) }], tourActuel: 10, contextePrecedent });
        setConvMessages(prev => [...prev, { role: 'assistant', content: "Parfait, j'ai tout ce qu'il me faut. Voici ton positionnement..." }]);
        setSynthese(res.message.replace(/^\s*(\*\*)?VALID[EÉ]\s*:\*?\*?\s*/i, '').trim());
        setPhase('done');
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setConvLoading(false);
    }
  };

  const handleKeyDown = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendConvMessage(); } };
  const currentBloc = QUESTIONS_CONV[Math.min(convStep, QUESTIONS_CONV.length - 1)].bloc;

  return (
    <div className="mp">
      <style>{BASE_CSS}</style>
      <style>{CSS}</style>
      <div className="mp-progress"><div className="mp-progress-fill" style={{ width: `${progress}%` }}></div></div>

      {phase === 'formation' && (
        <div className="mp-formation">
          {FORMATION.map((part, idx) => idx <= formationStep && (
            <div key={idx} className="mp-part">
              <div className="mp-part-num">{part.num}</div>
              <h2>{part.titre}</h2>
              {part.content()}
            </div>
          ))}
          <div className="mp-nav-btns">
            {formationStep > 0 && <button className="mp-btn-secondary" onClick={() => setFormationStep(s => s - 1)}>← Précédent</button>}
            <div style={{ flex: 1 }} />
            {formationStep < FORMATION.length - 1
              ? <button className="mp-btn-primary" onClick={() => setFormationStep(s => s + 1)}>Partie suivante</button>
              : <button className="mp-btn-primary" onClick={() => setPhase('quiz')}>Valider mes connaissances</button>}
          </div>
        </div>
      )}

      {phase === 'quiz' && (
        <div className="mp-quiz">
          <div className="mp-part-num" style={{ marginBottom: 20 }}>Quiz de validation</div>
          <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 'clamp(20px,2.5vw,26px)', color: '#173B34', marginBottom: 24 }}>Vérifions que les bases sont bien là.</h2>
          {QUIZ.map((q, qi) => (
            <div key={qi} className="mp-quiz-q">
              <div className="mp-quiz-q-text">{qi + 1}. {q.q}</div>
              <div className="mp-quiz-options">
                {q.opts.map((opt, oi) => {
                  let cls = 'mp-quiz-opt';
                  if (quizSubmitted) { if (oi === q.correct) cls += ' correct'; else if (quizAnswers[qi] === oi) cls += ' wrong'; }
                  else if (quizAnswers[qi] === oi) cls += ' selected';
                  return <div key={oi} className={cls} onClick={() => !quizSubmitted && setQuizAnswers(a => ({ ...a, [qi]: oi }))}>{opt}</div>;
                })}
              </div>
              {quizSubmitted && <div className={`mp-quiz-feedback ${quizAnswers[qi] === q.correct ? 'ok' : 'ko'}`}>{quizAnswers[qi] === q.correct ? '✅ ' : '❌ '}{q.feedback}</div>}
            </div>
          ))}
          {error && <div style={{ color: '#DC2626', fontSize: 13, marginBottom: 16 }}>{error}</div>}
          <div className="mp-nav-btns">
            <button className="mp-btn-secondary" onClick={() => setPhase('formation')}>← Revoir le cours</button>
            {!quizSubmitted ? <button className="mp-btn-primary" onClick={submitQuiz}>Valider mes réponses</button>
              : quizOk ? <button className="mp-btn-primary" onClick={() => setPhase('conversation')}>Parfait, on démarre avec Clara</button>
              : <div style={{ display: 'flex', gap: 10 }}>
                  <button className="mp-btn-secondary" onClick={retryQuiz}>Réessayer</button>
                  <button className="mp-btn-primary" onClick={() => setPhase('conversation')}>Continuer quand même →</button>
                </div>}
          </div>
        </div>
      )}

      {phase === 'conversation' && (
        <div className="mp-conv">
          <div className="mp-step-indicator">
            <div className="mp-step-dots">
              {QUESTIONS_CONV.map((_, i) => <div key={i} className={`mp-step-dot ${i < convStep ? 'done' : i === convStep ? 'active' : ''}`} />)}
            </div>
            Question {Math.min(convStep + 1, QUESTIONS_CONV.length)} / {QUESTIONS_CONV.length} · {currentBloc}
          </div>
          <div className="mp-messages">
            {convMessages.map((msg, i) => (
              <div key={i} className={`mp-msg ${msg.role === 'assistant' ? 'clara' : 'user'}`}>
                <div className="mp-msg-avatar">{msg.role === 'assistant' ? 'C' : '✎'}</div>
                <div className="mp-msg-bubble" style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</div>
              </div>
            ))}
            {convLoading && (
              <div className="mp-msg clara"><div className="mp-msg-avatar">C</div><div className="mp-msg-bubble" style={{ color: '#AAA' }}>Clara réfléchit...</div></div>
            )}
            {error && <div style={{ background: '#FDECEA', border: '1px solid #F5C2BB', color: '#9B2C1F', borderRadius: 12, padding: '10px 14px', fontSize: 14, margin: '8px 0' }}>{error} Réessaie en renvoyant ton message.</div>}
            <div ref={chatEndRef} />
          </div>
          <div className="mp-input-wrap">
            <textarea value={convInput} onChange={e => setConvInput(e.target.value)} onKeyDown={handleKeyDown} placeholder="Écris ta réponse à Clara" rows={2} disabled={convLoading} />
            <button className="mp-send-btn" onClick={sendConvMessage} disabled={convLoading || !convInput.trim()}>Envoyer</button>
          </div>
        </div>
      )}

      {phase === 'done' && (
        <div>
          <FichePositionnement synthese={synthese} brand={ctx.brand} />
          <div className="mp-nav-btns" style={{ marginTop: 24 }}>
            <button className="mp-btn-secondary" onClick={() => { setPhase('conversation'); setConvMessages([]); setConvStep(0); setSynthese(''); }}>Recommencer</button>
            {!moduleValide && <button className="mp-btn-primary" onClick={() => onValidate(synthese)}>Valider et continuer →</button>}
          </div>
        </div>
      )}
    </div>
  );
}
